import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../features/auth/data/services/auth.service';
import { Store } from '@ngrx/store';
import { loginSuccess } from '../../features/auth/state/auth.actions';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const http = inject(HttpClient);
  const router = inject(Router);
  const spinner = inject(NgxSpinnerService);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);
  const authService = inject(AuthService);
  const store = inject(Store);

  spinner.show('main');

  const currentRoute = state.url;

  return authService.getToken().pipe(
    switchMap((internalToken) => {
      if (!internalToken) {
        return authService.refreshToken().pipe(
          switchMap((res) => {
            store.dispatch(loginSuccess({ user: res.user, token: res.token }));
            spinner.hide('main');
            return of(true as boolean | UrlTree);
          }),
          catchError(() => {
            spinner.hide('main');
            return of(router.createUrlTree(['/login']));
          }),
        );
      }

      return authService.checkAuth().pipe(
        switchMap((auth) => {
          if (!auth.isAuthenticated || !auth.accessToken) {
            spinner.hide('main');
            return of(true as boolean | UrlTree);
          }

          const headers = new HttpHeaders({
            Authorization: `Bearer ${auth.accessToken}`,
            'X-Skip-Interceptor': 'true',
          });

          return http
            .get<{ success: boolean }>(
              `${environment.apiUrl}api/resources/check-access?route=${encodeURIComponent(currentRoute)}`,
              {
                headers,
                withCredentials: true,
              },
            )
            .pipe(
              map((res) => {
                if (!res.success) {
                  toastr.warning(
                    translate.instant('toastr.resourceAccess'),
                    translate.instant('toastr.accessDenied'),
                  );
                  return router.createUrlTree(['/no-access']);
                }

                return true;
              }),
              catchError((err) => {
                if (err.status === 401) {
                  return of(true as boolean | UrlTree);
                }

                if (err.status === 403) {
                  toastr.warning(
                    translate.instant('toastr.accessDenied'),
                    translate.instant('toastr.forbidden'),
                  );
                  return of(router.createUrlTree(['/no-access']));
                }

                toastr.error(
                  translate.instant('toastr.unexpectedError'),
                  translate.instant('toastr.error'),
                );
                return of(router.createUrlTree(['/login']));
              }),
              finalize(() => spinner.hide('main')),
            );
        }),
      );
    }),
    catchError(() => {
      toastr.error(
        translate.instant('toastr.authenticationFailed'),
        translate.instant('toastr.error'),
      );
      spinner.hide('main');
      return of(router.createUrlTree(['/login']));
    }),
  );
};
